import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { can, type Capability, type SessionInfo } from '@skhub/contracts';
import { api, ApiError, SESSION_EXPIRED_EVENT, setCsrfToken } from '@/lib/api';

interface AuthState {
  session: SessionInfo | null;
  loading: boolean;
  error: ApiError | null;
  can: (c: Capability) => boolean;
  signInDev: (email: string) => Promise<SessionInfo>;
  /** Exchanges a Microsoft ID token (SPA flow) for a Hub session. */
  signInEntra: (idToken: string) => Promise<SessionInfo>;
  signOut: () => Promise<void>;
  refresh: () => Promise<unknown>;
}

const AuthContext = createContext<AuthState | null>(null);

async function fetchSession(): Promise<SessionInfo | null> {
  try {
    const s = await api.get<SessionInfo | undefined>('/auth/session');
    if (!s) return null; // 204: not signed in
    setCsrfToken(s.csrfToken);
    return s;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['session'], queryFn: fetchSession, staleTime: 60_000, retry: 1, refetchOnWindowFocus: true });

  useEffect(() => {
    const onExpired = () => {
      setCsrfToken('');
      qc.setQueryData(['session'], null);
      sessionStorage.setItem('skhub.expired', '1');
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [qc]);

  const startSession = useCallback(
    async (path: string, body: object) => {
      await api.post(path, body);
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
      // staleTime 0: the cached signed-out `null` must not satisfy this read.
      const s = await qc.fetchQuery({ queryKey: ['session'], queryFn: fetchSession, staleTime: 0 });
      if (!s) throw new ApiError(401, 'unauthenticated', 'Sign-in did not start a session. Try again.');
      return s;
    },
    [qc],
  );
  const signInDev = useCallback((email: string) => startSession('/auth/dev-signin', { email }), [startSession]);
  const signInEntra = useCallback((idToken: string) => startSession('/auth/entra/token', { idToken }), [startSession]);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/signout');
    } catch (e) {
      // Only "already signed out" counts as done. Any other failure leaves the server session alive, so stay
      // signed in and let the caller say so; clearing locally would let the next focus sign the user back in.
      if (!(e instanceof ApiError && e.status === 401)) throw e;
    }
    {
      setCsrfToken('');
      // Session first, so no observer can briefly see the old user; then drop all other data.
      qc.setQueryData(['session'], null);
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
    }
  }, [qc]);

  const session = q.data ?? null;
  const value: AuthState = {
    session,
    loading: q.isPending,
    error: q.error instanceof ApiError ? q.error : q.error ? new ApiError(0, 'unknown', 'Could not check your session.') : null,
    can: (c) => can(session?.user.role, c),
    signInDev,
    signInEntra,
    signOut,
    refresh: () => q.refetch(),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
